import { Link } from "react-router-dom";
import { FileText, Camera, MessageSquare, Clock, Shield, CheckCircle, AlertCircle, Download } from "lucide-react";
import Header from "../components/Header";
import Footer from "../components/Footer";
import SEO from '../components/SEO';

const SCHEMA = {
  '@context': 'https://schema.org',
  '@type': 'HowTo',
  'name': 'Mobbing dokumentieren: Schritt-für-Schritt-Anleitung',
  'description': 'So dokumentierst du Mobbing am Arbeitsplatz oder in der Schule richtig — mit Beweisen, die vor Gericht und beim Betriebsrat zählen.',
  'step': [
    { '@type': 'HowToStep', 'name': 'Vorfälle sofort aufschreiben', 'text': 'Notiere jeden Vorfall so schnell wie möglich mit Datum, Uhrzeit, Ort, Beteiligten und genauen Aussagen.' },
    { '@type': 'HowToStep', 'name': 'Screenshots und Beweise sichern', 'text': 'Sichere digitale Beweise wie WhatsApp-Nachrichten, E-Mails oder Social-Media-Posts als Screenshot.' },
    { '@type': 'HowToStep', 'name': 'Zeugen festhalten', 'text': 'Notiere Namen von Personen, die Vorfälle beobachtet haben.' },
    { '@type': 'HowToStep', 'name': 'Protokoll führen', 'text': 'Führe ein lückenloses Mobbing-Protokoll mit allen Vorfällen chronologisch.' },
    { '@type': 'HowToStep', 'name': 'Meldung einreichen', 'text': 'Reiche das Protokoll bei mobbing-melden.de ein — anonym und kostenlos.' },
  ]
};

const STEPS = [
  {
    number: 1,
    icon: Clock,
    color: "#A0E8AF",
    title: "Sofort aufschreiben",
    description: "Notiere jeden Vorfall direkt danach: Datum, Uhrzeit, Ort, wer dabei war und was genau gesagt oder getan wurde. Je genauer, desto besser.",
    tip: "Nutze eine Notiz-App auf dem Handy — so hast du es immer dabei."
  },
  {
    number: 2,
    icon: Camera,
    color: "#BDE0FE",
    title: "Digitale Beweise sichern",
    description: "Screenshots von beleidigenden Nachrichten, E-Mails, WhatsApp-Chats oder Social-Media-Posts. Speichere sie an einem sicheren Ort (Cloud, externe Festplatte).",
    tip: "Nutze auf dem Smartphone 'Screenshot + Speichern' — nicht nur im Chat lassen."
  },
  {
    number: 3,
    icon: MessageSquare,
    color: "#FFC4D6",
    title: "Zeugen notieren",
    description: "Wer hat den Vorfall beobachtet? Name, Abteilung oder Klasse und was sie gesehen haben. Zeugenaussagen sind wertvolle Beweise.",
    tip: "Frag Kollegen oder Mitschüler diskret, ob sie bereit wären, auszusagen."
  },
  {
    number: 4,
    icon: FileText,
    color: "#F6E05E",
    title: "Protokoll führen",
    description: "Sammle alle Vorfälle chronologisch in einem Dokument. Ein lückenloses Protokoll zeigt Muster — das ist entscheidend für den Betriebsrat, Anwalt oder die Polizei.",
    tip: "Unser Meldeformular erstellt automatisch ein strukturiertes PDF-Protokoll."
  },
  {
    number: 5,
    icon: Shield,
    color: "#A0E8AF",
    title: "Meldung einreichen",
    description: "Reiche dein Protokoll anonym bei uns ein. Wir prüfen den Fall und helfen dir beim nächsten Schritt — kostenlos und vertraulich.",
    tip: "Du kannst Dateien bis zu 50 MB hochladen — inklusive Screenshots und Fotos."
  },
];

const EVIDENCE_TYPES = [
  { icon: "📱", label: "Screenshots", desc: "WhatsApp, SMS, Social Media" },
  { icon: "📧", label: "E-Mails", desc: "Beleidigend oder bedrohend" },
  { icon: "📝", label: "Notizen", desc: "Datum, Ort, Beteiligte, Aussagen" },
  { icon: "🎥", label: "Videos/Fotos", desc: "Als Beweis für Vorfälle" },
  { icon: "👥", label: "Zeugen", desc: "Namen und Beobachtungen" },
  { icon: "📋", label: "Dokumente", desc: "Abmahnungen, Protokolle" },
];

export default function MobbingDokumentieren() {
  return (
    <div className="min-h-screen bg-[#FFFDF9]">
      <Header />
      <SEO
        title="Mobbing dokumentieren: Anleitung + kostenlose Hilfe"
        description="So dokumentierst du Mobbing richtig: Beweise sichern, Protokoll führen, Zeugen notieren. Kostenlose Schritt-für-Schritt-Anleitung + anonymes Meldetool."
        path="/mobbing-dokumentieren"
        schema={SCHEMA}
      />

      <main className="px-6 md:px-12 lg:px-24 py-12">
        <div className="max-w-4xl mx-auto">

          {/* Hero */}
          <div className="text-center mb-12">
            <div className="w-20 h-20 bg-[#A0E8AF]/30 rounded-full flex items-center justify-center mx-auto mb-6">
              <FileText className="w-10 h-10 text-[#1A4731]" />
            </div>
            <h1 className="text-3xl md:text-4xl font-bold text-[#2D3748] mb-4" style={{ fontFamily: 'Nunito' }}>
              Mobbing dokumentieren: So geht es richtig
            </h1>
            <p className="text-lg text-[#718096] max-w-2xl mx-auto">
              Eine lückenlose Dokumentation ist dein wichtigstes Werkzeug — ob beim Betriebsrat, beim Anwalt oder bei der Polizei. Hier erfährst du, wie du es richtig machst.
            </p>
          </div>

          {/* Why document */}
          <div className="bg-[#FFF5F7] border border-[#FFC4D6] rounded-2xl p-6 mb-10 flex gap-4">
            <AlertCircle className="w-6 h-6 text-[#C53030] flex-shrink-0 mt-0.5" />
            <div>
              <h2 className="font-bold text-[#2D3748] mb-1">Warum Dokumentation so wichtig ist</h2>
              <p className="text-[#718096] text-sm">
                Ohne Beweise ist Mobbing schwer nachzuweisen. Arbeitgeber, Betriebsräte und Gerichte verlangen konkrete Fakten: Wann? Wo? Was genau? Wer war dabei? Ein gut geführtes Protokoll schützt dich und stärkt deine Position erheblich.
              </p>
            </div>
          </div>

          {/* Evidence types */}
          <div className="mb-12">
            <h2 className="text-2xl font-bold text-[#2D3748] mb-6" style={{ fontFamily: 'Nunito' }}>
              Diese Beweise zählen
            </h2>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
              {EVIDENCE_TYPES.map((item, i) => (
                <div key={i} className="bg-white rounded-xl border border-gray-100 p-4 text-center">
                  <div className="text-3xl mb-2">{item.icon}</div>
                  <div className="font-bold text-[#2D3748] text-sm mb-1">{item.label}</div>
                  <div className="text-[#A0AEC0] text-xs">{item.desc}</div>
                </div>
              ))}
            </div>
          </div>

          {/* Step-by-step */}
          <div className="mb-12">
            <h2 className="text-2xl font-bold text-[#2D3748] mb-6" style={{ fontFamily: 'Nunito' }}>
              Schritt für Schritt: So dokumentierst du richtig
            </h2>
            <div className="space-y-6">
              {STEPS.map((step, i) => {
                const Icon = step.icon;
                return (
                  <div key={i} className="bg-white rounded-2xl border border-gray-100 p-6 flex gap-5">
                    <div
                      className="w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0 text-white font-bold text-lg"
                      style={{ backgroundColor: step.color }}
                    >
                      {step.number}
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        <Icon className="w-5 h-5 text-[#A0AEC0]" />
                        <h3 className="font-bold text-[#2D3748]">{step.title}</h3>
                      </div>
                      <p className="text-[#718096] mb-3 text-sm">{step.description}</p>
                      <div className="bg-gray-50 rounded-lg px-4 py-2 text-xs text-[#718096]">
                        💡 <strong>Tipp:</strong> {step.tip}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Checklist */}
          <div className="bg-[#E6FFFA] rounded-2xl p-6 mb-10">
            <h2 className="text-xl font-bold text-[#2D3748] mb-4 flex items-center gap-2" style={{ fontFamily: 'Nunito' }}>
              <CheckCircle className="w-6 h-6 text-[#1A4731]" />
              Checkliste: Was gehört ins Protokoll?
            </h2>
            <ul className="space-y-2 text-sm text-[#4A5568]">
              {[
                "Datum und genaue Uhrzeit des Vorfalls",
                "Ort (Büro, Kantine, Schulhof, online, …)",
                "Beteiligte Personen (Name, Position/Klasse)",
                "Was genau gesagt oder getan wurde (wörtlich, wenn möglich)",
                "Namen von Zeugen",
                "Deine emotionale Reaktion und Auswirkungen (Schlafprobleme, Krankheit, …)",
                "Relevante Dokumente, Screenshots oder Fotos als Anhang",
              ].map((item, i) => (
                <li key={i} className="flex items-start gap-2">
                  <CheckCircle className="w-4 h-4 text-[#1A4731] flex-shrink-0 mt-0.5" />
                  {item}
                </li>
              ))}
            </ul>
          </div>

          {/* CTA */}
          <div className="bg-gradient-to-br from-[#E6FFFA] to-[#EBF8FF] rounded-2xl p-8 text-center">
            <h2 className="text-2xl font-bold text-[#2D3748] mb-3" style={{ fontFamily: 'Nunito' }}>
              Jetzt anonym dokumentieren und melden
            </h2>
            <p className="text-[#718096] mb-6 max-w-xl mx-auto">
              Unser Formular führt dich durch alle wichtigen Punkte und erstellt automatisch ein strukturiertes PDF-Protokoll — kostenlos und anonym.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/melden"
                className="inline-flex items-center justify-center gap-2 bg-[#A0E8AF] text-[#1A4731] font-bold px-8 py-4 rounded-full hover:bg-[#86D696] transition-all"
              >
                <FileText className="w-5 h-5" />
                Jetzt Meldung erstellen
              </Link>
              <Link
                to="/mobbing-protokoll"
                className="inline-flex items-center justify-center gap-2 border-2 border-[#BDE0FE] text-[#1A365D] font-bold px-8 py-4 rounded-full hover:bg-[#BDE0FE] transition-all"
              >
                Protokoll-Vorlage ansehen
              </Link>
            </div>
          </div>

        </div>
      </main>
      <Footer />
    </div>
  );
}
